/**
 * Schemi Zod condivisi client/server.
 *
 * Una sola fonte di verita': il web valida il form con lo stesso schema che l'API usa
 * per rifiutare la richiesta. La validazione server-side resta comunque obbligatoria
 * (il client non e' fidato); qui si evita solo che le due divergano.
 */

import { z } from 'zod';
import {
  BUDGET_CATEGORIES,
  BUDGET_MODES,
  DIETS,
  DISCOVER_SECTIONS,
  INTERESTS,
  ITEM_KINDS,
  MEAL_SLOTS,
  PLACE_CATEGORIES,
  TRANSPORT_MODES,
  TRAVEL_PACES,
  TRAVEL_PARTIES,
  TRIP_STATUSES,
} from './domain.js';
import { TRIP_ROLES } from './rbac.js';
import { isValidLocalTime, isValidTimeZone } from './time.js';

/* -------------------------------------------------------------------------- */
/* Primitivi                                                                   */
/* -------------------------------------------------------------------------- */

export const uuidSchema = z.string().uuid('identificativo non valido');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Email troppo corta')
  .max(254, 'Email troppo lunga')
  .email("Controlla l'indirizzo email");

/**
 * Password: lunghezza minima 10 (piu' efficace della complessita' forzata) e
 * massimo 200 per non trasformare l'hashing in un vettore di DoS.
 */
export const passwordSchema = z
  .string()
  .min(10, 'La password deve avere almeno 10 caratteri')
  .max(200, "La password e' troppo lunga");

export const displayNameSchema = z
  .string()
  .trim()
  .min(1, 'Serve un nome')
  .max(80, 'Nome troppo lungo');

export const currencySchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Codice valuta non valido (es. EUR)');

export const timezoneSchema = z
  .string()
  .trim()
  .refine(isValidTimeZone, 'Fuso orario non valido');

export const localTimeSchema = z
  .string()
  .trim()
  .refine(isValidLocalTime, 'Orario non valido (formato HH:MM)');

export const isoInstantSchema = z
  .string()
  .datetime({ offset: true })
  .or(z.string().datetime())
  .describe('istante ISO 8601');

export const latSchema = z.number().min(-90).max(90);
export const lonSchema = z.number().min(-180).max(180);

export const amountMinorSchema = z
  .number()
  .int('Gli importi sono interi in centesimi')
  .min(0, 'Importo negativo non ammesso')
  .max(1_000_000_000_000, 'Importo fuori scala');

/* -------------------------------------------------------------------------- */
/* Auth                                                                        */
/* -------------------------------------------------------------------------- */

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
  locale: z.string().trim().min(2).max(10).default('it-IT'),
  baseCurrency: currencySchema.default('EUR'),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Inserisci la password').max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

/* -------------------------------------------------------------------------- */
/* Destinazione                                                                */
/* -------------------------------------------------------------------------- */

/**
 * In Milestone 1 la destinazione arriva dall'onboarding come dato grezzo.
 * Il timezone e' OBBLIGATORIO: senza fuso non si puo' pianificare nulla e non lo
 * si inventa (vedi ARCHITECTURE.md §C9).
 */
export const destinationInputSchema = z.object({
  name: z.string().trim().min(1, 'Serve una destinazione').max(120),
  countryCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Codice paese non valido')
    .nullish(),
  timezone: timezoneSchema,
  lat: latSchema,
  lon: lonSchema,
  osmRelationId: z.number().int().positive().nullish(),
});
export type DestinationInput = z.infer<typeof destinationInputSchema>;

/* -------------------------------------------------------------------------- */
/* Viaggio                                                                     */
/* -------------------------------------------------------------------------- */

export const mealsEnabledSchema = z.object({
  breakfast: z.boolean(),
  lunch: z.boolean(),
  dinner: z.boolean(),
});

const tripCoreSchema = z.object({
  name: z.string().trim().min(1, 'Dai un nome al viaggio').max(120),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  party: z.enum(TRAVEL_PARTIES).default('solo'),
  partySize: z.number().int().min(1).max(30).default(1),
  pace: z.enum(TRAVEL_PACES).default('balanced'),
  interests: z.array(z.enum(INTERESTS)).max(INTERESTS.length).default([]),
  diets: z.array(z.enum(DIETS)).max(DIETS.length).default([]),
  transportModes: z.array(z.enum(TRANSPORT_MODES)).min(1, 'Scegli almeno un mezzo').default(['walk']),
  baseCurrency: currencySchema.default('EUR'),
  safetyBufferMin: z.number().int().min(0).max(120).default(15),
  dayStartLocal: localTimeSchema.default('09:00'),
  maxReturnTime: localTimeSchema.nullish(),
  mealsEnabled: mealsEnabledSchema.default({ breakfast: true, lunch: true, dinner: true }),
});

/**
 * Durata massima 60 giorni: oltre, il planning per giornata perde senso e il costo
 * di generazione esplode. Limite esplicito, non un crash silenzioso.
 */
const MAX_TRIP_DAYS = 60;

export const createTripSchema = tripCoreSchema
  .extend({ destination: destinationInputSchema })
  .superRefine((value, ctx) => {
    if (value.endsAt.getTime() <= value.startsAt.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endsAt'],
        message: "La partenza deve essere successiva all'arrivo",
      });
      return;
    }
    const days = (value.endsAt.getTime() - value.startsAt.getTime()) / 86_400_000;
    if (days > MAX_TRIP_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endsAt'],
        message: `Al momento supportiamo viaggi fino a ${MAX_TRIP_DAYS} giorni`,
      });
    }
  });
export type CreateTripInput = z.infer<typeof createTripSchema>;

export const updateTripSchema = tripCoreSchema
  .partial()
  .extend({ status: z.enum(TRIP_STATUSES).optional() })
  .superRefine((value, ctx) => {
    if (value.startsAt !== undefined && value.endsAt !== undefined) {
      if (value.endsAt.getTime() <= value.startsAt.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endsAt'],
          message: "La partenza deve essere successiva all'arrivo",
        });
      }
    }
  });
export type UpdateTripInput = z.infer<typeof updateTripSchema>;

/* -------------------------------------------------------------------------- */
/* Alloggio                                                                    */
/* -------------------------------------------------------------------------- */

const accommodationCoreSchema = z
  .object({
    name: z.string().trim().min(1, 'Serve un nome').max(160),
    address: z.string().trim().max(300).nullish(),
    lat: latSchema.nullish(),
    lon: lonSchema.nullish(),
    checkInAt: z.coerce.date().nullish(),
    checkOutAt: z.coerce.date().nullish(),
    costAmountMinor: amountMinorSchema.nullish(),
    costCurrency: currencySchema.nullish(),
    bookingReference: z.string().trim().max(120).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    // Contatti: servono quando qualcosa va storto all'arrivo.
    phone: z.string().trim().max(40).nullish(),
    email: z.string().trim().email('Indirizzo email non valido').max(160).nullish().or(z.literal('')),
    website: z.string().trim().url('Indirizzo del sito non valido').max(300).nullish().or(z.literal('')),
  });

const accommodationRefined = accommodationCoreSchema
  .superRefine((value, ctx) => {
    const hasLat = value.lat !== null && value.lat !== undefined;
    const hasLon = value.lon !== null && value.lon !== undefined;
    if (hasLat !== hasLon) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lon'],
        message: 'Servono entrambe le coordinate, oppure nessuna',
      });
    }
    const hasAmount = value.costAmountMinor !== null && value.costAmountMinor !== undefined;
    const hasCurrency = value.costCurrency !== null && value.costCurrency !== undefined;
    if (hasAmount && !hasCurrency) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['costCurrency'],
        message: 'Indica la valuta del costo',
      });
    }
    if (
      value.checkInAt !== null && value.checkInAt !== undefined &&
      value.checkOutAt !== null && value.checkOutAt !== undefined &&
      value.checkOutAt.getTime() <= value.checkInAt.getTime()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['checkOutAt'],
        message: 'Il check-out deve essere dopo il check-in',
      });
    }
  });

export const accommodationSchema = accommodationRefined;
export type AccommodationInput = z.infer<typeof accommodationSchema>;

/**
 * Modifica parziale: si aggiorna solo cio' che viene passato.
 *
 * Si parte dall'oggetto e non dallo schema completo perche' quest'ultimo porta
 * con se' i controlli incrociati (coordinate in coppia, date ordinate), che su
 * un aggiornamento parziale non hanno i dati per essere valutati.
 */
export const accommodationPatchSchema = accommodationCoreSchema.partial();
export type AccommodationPatchInput = z.infer<typeof accommodationPatchSchema>;

/* -------------------------------------------------------------------------- */
/* Membri e inviti                                                             */
/* -------------------------------------------------------------------------- */

/** Non si puo' invitare o promuovere qualcuno a owner: l'owner e' uno solo. */
export const assignableRoleSchema = z.enum(['editor', 'viewer']);

export const createInviteSchema = z.object({
  role: assignableRoleSchema.default('editor'),
  expiresInHours: z.number().int().min(1).max(24 * 30).default(24 * 7),
  maxUses: z.number().int().min(1).max(50).default(10),
});
export type CreateInviteInput = z.infer<typeof createInviteSchema>;

export const acceptInviteSchema = z.object({
  token: z.string().trim().min(20, 'Invito non valido').max(200),
});

export const changeRoleSchema = z.object({ role: assignableRoleSchema });

export const tripRoleSchema = z.enum(TRIP_ROLES);

/* -------------------------------------------------------------------------- */
/* Budget (schemi pronti per Milestone 6, gia' condivisi per l'onboarding)      */
/* -------------------------------------------------------------------------- */

export const budgetSetupSchema = z
  .object({
    mode: z.enum(BUDGET_MODES).default('none'),
    totalMinor: amountMinorSchema.nullish(),
    dailyMinor: amountMinorSchema.nullish(),
    currency: currencySchema.default('EUR'),
    categories: z
      .array(
        z.object({
          category: z.enum(BUDGET_CATEGORIES),
          plannedMinor: amountMinorSchema,
        }),
      )
      .default([]),
  })
  .superRefine((value, ctx) => {
    const needsTotal = value.mode === 'total' || value.mode === 'per_person';
    if (needsTotal && (value.totalMinor === null || value.totalMinor === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totalMinor'],
        message: 'Indica il budget totale',
      });
    }
    if (value.mode === 'daily' && (value.dailyMinor === null || value.dailyMinor === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dailyMinor'],
        message: 'Indica il budget giornaliero',
      });
    }
  });
export type BudgetSetupInput = z.infer<typeof budgetSetupSchema>;

export const mealSlotSchema = z.enum(MEAL_SLOTS);

/* -------------------------------------------------------------------------- */
/* Luoghi e ricerca                                                            */
/* -------------------------------------------------------------------------- */

export const placeSearchSchema = z.object({
  section: z.enum(DISCOVER_SECTIONS).optional(),
  category: z.enum(PLACE_CATEGORIES).optional(),
  q: z.string().trim().max(120).optional(),
  /** Punto di riferimento per distanza e ordinamento. */
  nearLat: z.coerce.number().min(-90).max(90).optional(),
  nearLon: z.coerce.number().min(-180).max(180).optional(),
  radiusM: z.coerce.number().int().min(100).max(60_000).default(15_000),
  openAt: z.coerce.date().optional(),
  maxPriceTier: z.enum(['€', '€€', '€€€']).optional(),
  freeOnly: z.coerce.boolean().default(false),
  /**
   * Voto minimo. Filtra soltanto se i voti sono attivi: senza un provider
   * configurato nessun luogo ha un voto, e l'interfaccia non offre il filtro
   * invece di mostrarne uno che svuoterebbe l'elenco.
   */
  minRating: z.coerce.number().min(1).max(5).optional(),
  /** Numero minimo di recensioni: un 5,0 su due voti non dice granché. */
  minRatingsCount: z.coerce.number().int().min(1).max(100_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).max(5000).default(0),
});
export type PlaceSearchInput = z.infer<typeof placeSearchSchema>;

/* -------------------------------------------------------------------------- */
/* Itinerario                                                                  */
/* -------------------------------------------------------------------------- */

export const createItineraryItemSchema = z
  .object({
    tripDayId: uuidSchema,
    placeId: uuidSchema.nullish(),
    kind: z.enum(ITEM_KINDS).default('poi'),
    title: z.string().trim().min(1, 'Serve un titolo').max(160),
    notes: z.string().trim().max(2000).nullish(),
    plannedStart: z.coerce.date().nullish(),
    durationMin: z.number().int().min(5).max(1440).default(60),
    isLocked: z.boolean().default(false),
    estimatedCostMinor: amountMinorSchema.nullish(),
    estimatedCostCurrency: currencySchema.nullish(),
    mealSlot: z.enum(MEAL_SLOTS).nullish(),
    lat: latSchema.nullish(),
    lon: lonSchema.nullish(),
  })
  .superRefine((value, ctx) => {
    // Una tappa "bloccata" senza orario non vuol dire nulla.
    if (value.isLocked && (value.plannedStart === null || value.plannedStart === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['plannedStart'],
        message: 'Per bloccare una tappa serve un orario',
      });
    }
    const hasLat = value.lat !== null && value.lat !== undefined;
    const hasLon = value.lon !== null && value.lon !== undefined;
    if (hasLat !== hasLon) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lon'],
        message: 'Servono entrambe le coordinate, oppure nessuna',
      });
    }
  });
export type CreateItineraryItemInput = z.infer<typeof createItineraryItemSchema>;

export const updateItineraryItemSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  notes: z.string().trim().max(2000).nullish(),
  plannedStart: z.coerce.date().nullish(),
  durationMin: z.number().int().min(5).max(1440).optional(),
  isLocked: z.boolean().optional(),
  estimatedCostMinor: amountMinorSchema.nullish(),
  estimatedCostCurrency: currencySchema.nullish(),
  tripDayId: uuidSchema.optional(),
});
export type UpdateItineraryItemInput = z.infer<typeof updateItineraryItemSchema>;

/** Riordino esplicito: l'elenco completo degli id nella sequenza voluta. */
export const reorderItemsSchema = z.object({
  tripDayId: uuidSchema,
  itemIds: z.array(uuidSchema).min(1).max(60),
});

export const generateDaySchema = z.object({
  /** Se false, le tappe non bloccate esistenti vengono conservate. */
  replaceExisting: z.boolean().default(true),
});

/* -------------------------------------------------------------------------- */
/* Budget e spese                                                              */
/* -------------------------------------------------------------------------- */

export const budgetInputSchema = z.object({
  scope: z.enum(['trip', 'personal']).default('trip'),
  totalMinor: amountMinorSchema.nullish(),
  dailyMinor: amountMinorSchema.nullish(),
  perPerson: z.boolean().default(false),
  currency: currencySchema.default('EUR'),
  categories: z
    .array(z.object({ category: z.enum(BUDGET_CATEGORIES), plannedMinor: amountMinorSchema }))
    .max(BUDGET_CATEGORIES.length)
    .default([]),
});
export type BudgetInput = z.infer<typeof budgetInputSchema>;

export const createExpenseSchema = z
  .object({
    tripDayId: uuidSchema.nullish(),
    itineraryItemId: uuidSchema.nullish(),
    category: z.enum(BUDGET_CATEGORIES).default('extra'),
    description: z.string().trim().min(1, 'Descrivi la spesa').max(200),
    amountMinor: amountMinorSchema,
    currency: currencySchema,
    fxRate: z.number().positive().max(1_000_000).default(1),
    paidBy: uuidSchema.nullish(),
    paidAt: z.coerce.date().optional(),
    isShared: z.boolean().default(false),
    /** Ripartizione: vuota = divisione uguale fra i partecipanti indicati. */
    splitUserIds: z.array(uuidSchema).max(30).default([]),
    splitShares: z
      .array(z.object({ userId: uuidSchema, shareMinor: amountMinorSchema }))
      .max(30)
      .default([]),
  })
  .superRefine((value, ctx) => {
    if (value.splitShares.length > 0) {
      const total = value.splitShares.reduce((sum, entry) => sum + entry.shareMinor, 0);
      if (total !== value.amountMinor) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['splitShares'],
          message: "La somma delle quote deve corrispondere esattamente all'importo",
        });
      }
    }
  });
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

/* -------------------------------------------------------------------------- */
/* Voli                                                                        */
/* -------------------------------------------------------------------------- */

const flightCoreSchema = z
  .object({
    direction: z.enum(['outbound', 'return', 'other']).default('outbound'),
    airline: z.string().trim().max(80).nullish(),
    flightNumber: z.string().trim().max(12).nullish(),
    depAirport: z.string().trim().min(2, 'Aeroporto di partenza').max(80),
    arrAirport: z.string().trim().min(2, 'Aeroporto di arrivo').max(80),
    depTerminal: z.string().trim().max(20).nullish(),
    arrTerminal: z.string().trim().max(20).nullish(),
    gate: z.string().trim().max(20).nullish(),
    depScheduled: z.coerce.date(),
    arrScheduled: z.coerce.date().nullish(),
    bookingReference: z.string().trim().max(40).nullish(),
    seat: z.string().trim().max(20).nullish(),
    notes: z.string().trim().max(1000).nullish(),
    depLat: latSchema.nullish(),
    depLon: lonSchema.nullish(),
    airportBufferMin: z.number().int().min(0).max(480).default(120),
    checkinOpensAt: z.coerce.date().nullish(),
    /**
     * Ordine della tratta dentro il viaggio aereo: 1 = prima tratta.
     * Con piu' tratte, fra l'arrivo di una e la partenza della successiva
     * l'app calcola il tempo di scalo.
     */
    legOrder: z.number().int().min(1).max(10).default(1),
  });

export const flightSchema = flightCoreSchema
  .superRefine((value, ctx) => {
    if (
      value.arrScheduled !== null &&
      value.arrScheduled !== undefined &&
      value.arrScheduled.getTime() <= value.depScheduled.getTime()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['arrScheduled'],
        message: "L'arrivo deve essere dopo la partenza",
      });
    }
  });
export type FlightInput = z.infer<typeof flightSchema>;

/** Modifica parziale di un volo: orari e gate cambiano di continuo. */
export const flightPatchSchema = flightCoreSchema.partial();
export type FlightPatchInput = z.infer<typeof flightPatchSchema>;

/* -------------------------------------------------------------------------- */
/* Checklist, prenotazioni, collaborazione                                     */
/* -------------------------------------------------------------------------- */

export const CHECKLIST_CATEGORIES = [
  'flights', 'accommodation', 'documents', 'insurance', 'tickets', 'transfers',
  'reservations', 'luggage', 'connectivity', 'payments', 'activities', 'other',
] as const;

export const checklistItemSchema = z.object({
  category: z.enum(CHECKLIST_CATEGORIES).default('other'),
  label: z.string().trim().min(1, 'Serve una descrizione').max(160),
  state: z.enum(['todo', 'done', 'missing', 'na']).default('todo'),
});

export const reservationSchema = z.object({
  itineraryItemId: uuidSchema.nullish(),
  placeId: uuidSchema.nullish(),
  kind: z
    .enum(['accommodation', 'museum', 'tour', 'train', 'restaurant', 'ticket', 'activity'])
    .default('activity'),
  title: z.string().trim().min(1, 'Serve un titolo').max(160),
  startsAt: z.coerce.date().nullish(),
  endsAt: z.coerce.date().nullish(),
  confirmationCode: z.string().trim().max(80).nullish(),
  costAmountMinor: amountMinorSchema.nullish(),
  costCurrency: currencySchema.nullish(),
  notes: z.string().trim().max(2000).nullish(),
});
export type ReservationInput = z.infer<typeof reservationSchema>;

export const proposalSchema = z.object({
  placeId: uuidSchema.nullish(),
  title: z.string().trim().min(1, 'Serve un titolo').max(160),
  note: z.string().trim().max(1000).nullish(),
});

export const voteSchema = z.object({ value: z.union([z.literal(1), z.literal(-1)]) });

export const commentSchema = z.object({
  body: z.string().trim().min(1, 'Scrivi qualcosa').max(2000),
});

export const pushSubscriptionSchema = z.object({
  endpoint: z.string().url().max(600),
  keys: z.object({
    p256dh: z.string().min(10).max(200),
    auth: z.string().min(4).max(100),
  }),
});
